import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Target, Eye, ArrowUpRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { COMPANY, IMAGES, USPS } from "../data/site";

export const About = ({ singlePage, company: companyProp, usps: uspsProp, qualityCerts, parallax }) => {
  const company = companyProp || COMPANY;
  const usps = uspsProp || USPS;
  const imgWrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgWrapRef,
    offset: ["start end", "end start"],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.0, 1.18]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  return (
    <section data-testid="about-section" id="about" className="relative py-24 md:py-32 bg-white scroll-mt-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
              <span className="w-8 h-px bg-amber-500" />
              About Nikhan Controls
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-navy-900 tracking-tighter leading-[1]">
              A quiet force in
              <br />
              <span className="text-amber-500">Indian electronics.</span>
            </h2>
            <p className="mt-8 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
              <span className="text-navy-900 font-semibold">{company.legal}</span>, established in{" "}
              <span className="text-amber-600 font-semibold">{company.established}</span> in Mohali,
              is a trusted name in embedded system design, power electronics and manufacturing
              solutions. From concept to production, we specialise in custom control panels,
              multi-layer PCBs, EV chargers, smart plugs, SMPS systems and transformers.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="rounded-2xl p-6 border border-slate-200 bg-slate-50/50 hover:border-amber-300 transition-colors">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-amber-600" />
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-600">
                    Vision
                  </div>
                </div>
                <p className="mt-4 text-navy-900 text-lg font-display font-semibold leading-snug tracking-tight">
                  {company.vision}
                </p>
              </div>
              <div className="rounded-2xl p-6 border border-slate-200 bg-slate-50/50 hover:border-amber-300 transition-colors">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-amber-600" />
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-600">
                    Mission
                  </div>
                </div>
                <p className="mt-4 text-navy-900 text-lg font-display font-semibold leading-snug tracking-tight">
                  {company.mission}
                </p>
              </div>
            </div>

            {singlePage ? (
              <a
                href="#factory"
                data-testid="about-explore-btn"
                className="group mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3"
              >
                Explore our factory
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </a>
            ) : (
              <Link
                to="/about"
                data-testid="about-explore-btn"
                className="group mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy-900 hover:bg-navy-800 text-white font-semibold tracking-tight transition-all hover:gap-3"
              >
                Explore our story
                <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              </Link>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div ref={imgWrapRef} className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-navy-900/15">
              <motion.img
                src={IMAGES.engineerCloseup}
                alt="Engineer at Nikhan Controls"
                style={parallax ? { scale: imgScale, y: imgY } : undefined}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-6 -left-6 hidden md:block z-10">
              <div className="bg-amber-500 text-navy-900 px-6 py-5 rounded-2xl shadow-xl">
                <div className="font-display text-4xl font-extrabold tracking-tighter leading-none">
                  {new Date().getFullYear() - company.established}+
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.18em] font-bold">
                  Years building<br />trust
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* USPs */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {usps.map((u, i) => (
            <motion.div
              key={u.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="lift-card rounded-2xl p-7 border border-slate-200 bg-white"
              data-testid={`usp-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 mb-5">
                <u.icon className="w-5 h-5" />
              </div>
              <h4 className="font-display text-lg font-bold text-navy-900 tracking-tight">
                {u.title}
              </h4>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                {u.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Certifications & Approvals — embedded subsection */}
        {qualityCerts && qualityCerts.length > 0 && (
          <div data-testid="about-quality-certs" className="mt-24 pt-16 border-t border-slate-200">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
            >
              <div className="max-w-2xl">
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
                  <span className="w-8 h-px bg-amber-500" />
                  Certifications & Approvals
                </div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 tracking-tight leading-tight">
                  Built to the standards that matter.
                </h3>
                <p className="mt-3 text-slate-600 text-sm md:text-base leading-relaxed">
                  Independently audited and certified — so the products we engineer for you ship with confidence.
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-mono whitespace-nowrap">
                <ShieldCheck className="w-4 h-4 text-amber-500" />
                Independently audited
              </div>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {qualityCerts.map((c, i) => (
                <motion.div
                  key={c.name}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  data-testid={`about-cert-${i}`}
                  className="group relative aspect-[4/3] bg-white rounded-2xl border border-slate-200 p-5 md:p-6 flex items-center justify-center hover:border-amber-500 hover:shadow-lg hover:-translate-y-1 transition-all duration-500"
                >
                  <img
                    src={c.logo}
                    alt={c.name}
                    loading="lazy"
                    className="max-w-full max-h-full object-contain"
                  />
                  <div className="absolute bottom-2 inset-x-3 text-center text-[10px] uppercase tracking-[0.22em] text-slate-500 group-hover:text-amber-600 font-bold opacity-0 group-hover:opacity-100 transition-all">
                    {c.name}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default About;
